<template>
  <div class="RiskManagement">
    <div class="d-flex justify-content-end">
      <button :disabled="isUpdating" class="ui-action-button" @click="save">
        {{ $t('global.saveChanges') }}
        <b-spinner class="ml-1" small label="loading" v-if="isUpdating"></b-spinner>
      </button>
    </div>

    <div class="RiskManagement__form" v-if="!isLoading">
      <div class="input-container" >
        <label class="text-black">{{ $t("integrityCompliance.RiskManagement.form_title") }} (*)</label>
        <input type="text" id="title" name="title" v-model="category.title" />
        <div class="ui-input-error-msg">
          <span v-if="$v.category.title.$error">{{ $t("global.inputs.required") }}</span>
        </div>
      </div>

      <div class="input-file">
        <label>{{ $t("integrityCompliance.Carga") }}</label>
        <FileUploader
          :key="fileUploaderKey"
          :fileUrl="originalFile.file_url"
          :placeholder="$t('integrityCompliance.file_required')"
          type=".pdf"
          validate="pdf"
          :label="$t('newAlert.browse')"
          @setFile="setFile"
        />
        <div class="ui-input-error-msg">
          <span v-if="$v.file.$error">{{ $t("global.inputs.required") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
const FileUploader = () => import("@/components/FileUploader");
import { required, requiredIf } from "vuelidate/lib/validators";
import { ICfilesName, integrityComplianceKey } from "@/lib/constants.js";
import API_SERVICE from "@/lib/api/IntegrityCompliance.js";
const api = new API_SERVICE();
import S3_Handler from "@/lib/api/s3_handler";
const s3 = new S3_Handler();

export default {
  inject: ['IC_getMainCategory'],
  components: {
    FileUploader
  },
  computed: {
    originalFile() {
      let file = {};
      const files = this.category && this.category.files
        ?  this.category.files
        : [];
      if (files && files.length) {
        file = files[files.length - 1]
      }
      return file;
    }
  },
  data: function () {
    return {
      title: '',
      category: {
        title: '',
        files: []
      },
      file: null,
      isUpdating: false,
      isLoading: false,
      fileUploaderKey: new Date().getTime()
    }
  },
  validations: {
    category: {
      title: {
        required
      },
    },
    file: {
      required: requiredIf(function() {
        if (this.originalFile.file_url) {
          return false;
        } else {
          return true;
        }
      })
    }
  },
  async mounted() {
    const category = this.IC_getMainCategory();
    this.category = {
      ...category,
      files: []
    }
    await this.getCategoryDetail();
  },
  methods: {
    async save() {
      this.category.title = this.category.title.trim();
      this.$v.$touch();
      if (this.$v.$error) return;

      try {
        this.isUpdating = true;

        await Promise.all([
          this.saveCategory(),
          this.saveFile(this.file)
        ])

        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('global.successSaved'),
          show: true,
          type: "success"
        });
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.RiskManagement.saveError'),
          show: true,
          type: "error"
        });
      } finally {
        this.isUpdating = false;
      }
    },
    async saveCategory () {
      try {
        const params = {
          name: integrityComplianceKey.riskManagement,
          title: this.category.title,
          description: this.category.description
        }
        const response = await api.updateCategory(this.category.id, params)
        this.$emit('categoryUpdated', {
          id: this.category.id,
          ...response
        });
        
      } catch (error) {
        throw error;
      }
    },
    async saveFile (file) {
      if (!file) return;
      try {
        const { Bucket, Key } = await this.uploadFile(file);
        const params = {
          name: ICfilesName[this.category.name],
          description: ICfilesName[this.category.name],
          srcBucket: Bucket,
          src: Key,
          category_id: this.category.id,
        }

        if (this.originalFile.id) { // update file
          await api.updateDocument(this.originalFile.id, params);
        } else { // save new file
          const response = await api.createDocument(params);
          this.category.files.push(response);
        }

        this.file = null;
        this.fileUploaderKey = `${integrityComplianceKey.riskManagement}_fileUploader_${new Date().getTime()}`
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('errors.file.upload'),
          show: true,
          type: "error"
        });
      }
    },
    setFile(file) {
      this.file = file;
      if (!file) {
        this.fileUploaderKey = `${integrityComplianceKey.riskManagement}_fileUploader_${new Date().getTime()}`
      }
    },
    async uploadFile(file) {
      if (!file) return;
      try {
        const timestamp = Date.now();
        const response = await s3.uploadFile(
          `IC/${this.category.name}/${timestamp}.pdf`,
          file
        );
        return response;
      } catch (error) {
        throw (error)
      }
    },
    async getCategoryDetail() {
      try {
        this.isLoading = true;
        const response = await api.getCategory(this.category.id);
        if (response) {
          this.category.files = response.files && response.files.length
            ? response.files
            : []
        }
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.RiskManagement.getError'),
          show: true,
          type: "error"
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.RiskManagement {
  width: 100%;

  &__form {
    margin-top: $global_padding;

    .input-container {
      width: 35%;
    }

    .input-file {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin: 20px 0px;
      width: 45%;

      label {
        @extend %audi-b;
      }
    }
  }
}
</style>
